import LegalLayout from './LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Veridex collects, uses and protects your personal data under the EU GDPR."
      lastUpdated="19 April 2026"
    >
      <h2>1. Controller</h2>
      <p>
        The controller for personal data processed through this Service is the Veridex team,
        contactable at <a href="mailto:privacy@veridex-one.com">privacy@veridex-one.com</a>.
      </p>

      <h2>2. What Data We Collect</h2>
      <ul>
        <li><strong>Account data</strong>: name, work email, company, country, preferred language, hashed password.</li>
        <li><strong>Business data you upload</strong>: invoices, supplier records, product master data, CSV imports, emission-factor overrides and derived PCF calculations.</li>
        <li><strong>AI interaction data</strong>: prompts and completions produced by CSV mapping, invoice extraction, supplier estimation and the in-app assistant.</li>
        <li><strong>Technical data</strong>: IP address, browser user-agent, referrer, pages visited, timestamps and error traces.</li>
        <li><strong>Billing data</strong>: processed by Stripe; we store only a customer ID, last-4, tier and subscription status.</li>
      </ul>

      <h2>3. Purposes and Legal Basis</h2>
      <ul>
        <li><strong>Delivering the Service</strong> (Art. 6(1)(b) GDPR — contract).</li>
        <li><strong>Security, fraud-prevention, rate-limiting and abuse monitoring</strong> (Art. 6(1)(f) — legitimate interest).</li>
        <li><strong>Product analytics and improvement</strong> with pseudonymous identifiers, gated by your cookie consent (Art. 6(1)(a) — consent).</li>
        <li><strong>Legal obligations</strong> including VAT invoicing and accounting retention (Art. 6(1)(c)).</li>
      </ul>

      <h2>4. Sub-processors</h2>
      <p>
        Veridex relies on the following sub-processors. Each has signed a data-processing
        addendum and — where required — EU Standard Contractual Clauses.
      </p>
      <ul>
        <li><strong>Emergent</strong> — hosting, container runtime and managed LLM key.</li>
        <li><strong>MongoDB</strong> — primary database.</li>
        <li><strong>Stripe</strong> — billing and subscription management.</li>
        <li><strong>Resend</strong> — transactional email delivery.</li>
        <li><strong>Google Analytics 4</strong> — pseudonymous product analytics (only if cookies accepted).</li>
        <li><strong>Sentry</strong> — error monitoring (production only).</li>
        <li><strong>HubSpot</strong> — CRM and marketing contact management (only if cookies accepted).</li>
        <li><strong>Tawk.to</strong> — in-product live chat (only if cookies accepted).</li>
      </ul>

      <h2>5. International Transfers</h2>
      <p>
        Some sub-processors (e.g. Stripe, Google) operate outside the EU/EEA. Transfers
        rely on EU Standard Contractual Clauses and, where applicable, supplementary
        technical and organizational measures.
      </p>

      <h2>6. Retention</h2>
      <ul>
        <li>Account and business data: for the lifetime of the account, then deleted within 30 days of termination, except where statutory retention (e.g. 10-year VAT/invoicing rule) applies.</li>
        <li>Telemetry and error traces: 90 days.</li>
        <li>Audit-trail / change-log records: retained for the statutory CBAM audit window (minimum 4 years).</li>
      </ul>

      <h2>7. Your Rights</h2>
      <p>
        Under the GDPR you may at any time:
      </p>
      <ul>
        <li><strong>Access</strong> — request a copy of your personal data.</li>
        <li><strong>Rectify</strong> — correct inaccurate data.</li>
        <li><strong>Erase</strong> — request deletion of your account and associated data.</li>
        <li><strong>Port</strong> — export your data in a machine-readable format.</li>
        <li><strong>Restrict / Object</strong> — restrict or object to certain processing.</li>
        <li><strong>Withdraw consent</strong> — revoke cookie or marketing consent at any time in Settings &rarr; Cookie preferences.</li>
        <li><strong>Complain</strong> — lodge a complaint with your local supervisory authority.</li>
      </ul>
      <p>
        To exercise any right, email{' '}
        <a href="mailto:privacy@veridex-one.com">privacy@veridex-one.com</a>. We will respond
        within 30 days.
      </p>

      <h2>8. Security</h2>
      <p>
        All traffic is encrypted with TLS 1.2+. Passwords are hashed with bcrypt. API keys
        and third-party credentials (DATEV, SAP) are encrypted at rest. We perform
        continuous dependency scanning and isolated per-tenant data access.
      </p>

      <h2>9. Cookies</h2>
      <p>
        Cookie usage is described in our <a href="/legal/cookies">Cookie Policy</a> and
        controlled by the banner on first visit.
      </p>

      <h2>10. Changes</h2>
      <p>
        We will notify you by email and in the product of any material change to this
        Privacy Policy before it takes effect.
      </p>
    </LegalLayout>
  );
}
